import type { AccessGuardMetricsEntry } from '@/lib/urm-api';

export interface AccessGuardDimensions {
  user: string | null;
  path: string | null;
  group: string | null;
  version: string | null;
  type: string | null;
  name: string | null;
  recognized: boolean;
}

export interface AccessGuardRow extends AccessGuardMetricsEntry {
  dimensions: AccessGuardDimensions;
}

export interface AccessGuardUserSummary {
  user: string | null;
  action: string;
  count: number;
}

export type AccessGuardSortField = 'count' | 'action' | 'user' | 'type' | 'name';
export type SortDirection = 'asc' | 'desc';

const emptyDimensions = (): AccessGuardDimensions => ({
  user: null,
  path: null,
  group: null,
  version: null,
  type: null,
  name: null,
  recognized: false,
});

// Keep this in sync with resources-server's getLimiterKey(): getEvents is
// user-scoped, touch is resource-scoped, and the remaining guards combine both.
export function parseAccessGuardLimiterKey(action: string, limiterKey: string): AccessGuardDimensions {
  if (action === 'getEvents' && limiterKey) {
    return { ...emptyDimensions(), user: limiterKey, recognized: true };
  }

  const parts = limiterKey.split(':');
  if (action === 'touch' && parts.length === 5 && parts.every(Boolean)) {
    const [path, group, version, type, name] = parts;
    return { user: null, path, group, version, type, name, recognized: true };
  }

  if (['read', 'write', 'getHistory'].includes(action)
    && parts.length === 6
    && parts.every(Boolean)) {
    const [user, path, group, version, type, name] = parts;
    return { user, path, group, version, type, name, recognized: true };
  }

  return emptyDimensions();
}

export function buildAccessGuardRows(entries: AccessGuardMetricsEntry[]): AccessGuardRow[] {
  return entries.map((entry) => ({
    ...entry,
    dimensions: parseAccessGuardLimiterKey(entry.action, entry.limiter_key),
  }));
}

function sortValue(row: AccessGuardRow, field: AccessGuardSortField): string | number {
  if (field === 'count') return row.count;
  if (field === 'action') return row.action;
  return row.dimensions[field] ?? '';
}

export function sortAccessGuardRows(
  rows: AccessGuardRow[],
  field: AccessGuardSortField,
  direction: SortDirection,
): AccessGuardRow[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    const leftValue = sortValue(left, field);
    const rightValue = sortValue(right, field);
    const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return comparison * multiplier
      || left.action.localeCompare(right.action)
      || left.limiter_key.localeCompare(right.limiter_key);
  });
}

export function aggregateAccessGuardRowsByUser(rows: AccessGuardRow[]): AccessGuardUserSummary[] {
  const summaries = new Map<string, AccessGuardUserSummary>();
  for (const row of rows) {
    const key = `${row.dimensions.user ?? ''}\u0000${row.action}`;
    const existing = summaries.get(key);
    if (existing) {
      existing.count += row.count;
    } else {
      summaries.set(key, {
        user: row.dimensions.user,
        action: row.action,
        count: row.count,
      });
    }
  }
  return Array.from(summaries.values());
}

export function sortAccessGuardUserSummaries(
  summaries: AccessGuardUserSummary[],
  field: 'count' | 'action' | 'user',
  direction: SortDirection,
): AccessGuardUserSummary[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...summaries].sort((left, right) => {
    const leftValue = field === 'user' ? left.user ?? '' : left[field];
    const rightValue = field === 'user' ? right.user ?? '' : right[field];
    const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return comparison * multiplier
      || (left.user ?? '').localeCompare(right.user ?? '')
      || left.action.localeCompare(right.action);
  });
}

function escapeCsvCell(value: string | number): string {
  let text = String(value);
  // Prevent spreadsheet applications from interpreting exported data as a formula.
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function resourceLabel(row: AccessGuardRow): string {
  const { dimensions } = row;
  if (!dimensions.recognized) return row.limiter_key;
  if (!dimensions.type) return 'All resources';
  return `${dimensions.type} / ${dimensions.name} (${dimensions.group}/${dimensions.version}) · ${dimensions.path}`;
}

export function accessGuardRowsToCsv(rows: AccessGuardRow[]): string {
  const records: Array<Array<string | number>> = [
    ['Action', 'User', 'Resource', 'Count'],
    ...rows.map((row) => [
      row.action,
      row.dimensions.user ?? '',
      resourceLabel(row),
      row.count,
    ]),
  ];
  return `\uFEFF${records.map((record) => record.map(escapeCsvCell).join(',')).join('\r\n')}\r\n`;
}

export function accessGuardUserSummariesToCsv(summaries: AccessGuardUserSummary[]): string {
  const records: Array<Array<string | number>> = [
    ['User', 'Bucket', 'Violations'],
    ...summaries.map((summary) => [
      summary.user ?? 'Resource-scoped (no user)',
      summary.action,
      summary.count,
    ]),
  ];
  return `\uFEFF${records.map((record) => record.map(escapeCsvCell).join(',')).join('\r\n')}\r\n`;
}
